import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'app/hooks'

import colors from 'utils/colors'
import { UserStatus } from 'types/user.types'
import { NAVIGATION, USER_STATUS } from 'locales/locales'
import { useUpdateUserStatusMutation } from 'app/services/user.service'
import CustomText from 'components/text/CustomText'
import Separator from 'components/separator/Separator'
import { clientChannel, LOGOUT } from 'utils/constants'
import { useLogoutMutation } from 'app/services/auth.service'
import { useEffect, useState } from 'react'
import { IRolePermissions, SettingsPermissions, TeamAndChannelPermissions, UserManagementPermissions } from 'types/role.types'
import { isFlagSetIn } from 'utils/helpers/isSetInFlags'
import { socket } from 'app/socket/socketContext'
import { removeDataFromDB } from 'utils/helpers/storageHelper'
import { FixMeLater } from 'types'
import { deleteFCMToken, getFCMToken, signOutFirebase } from 'services/firebase.service'

const Status = ({
  status,
  text,
  onClick,
}: {
  status: string
  text: string
  onClick: (status: string) => void
}) => {
  const onClickStatus = () => {
    onClick(status)
  }

  return (
    <StatusWrapper onClick={onClickStatus}>
      <StatusColor status={status} />
      <CustomText>{text}</CustomText>
    </StatusWrapper>
  )
}

const Options = ({
  onOpenAccountSettings,
  onHide,
  userOnly
}: {
  onOpenAccountSettings: () => void
  onHide: () => void
  userOnly?: boolean
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const permissions = useAppSelector<IRolePermissions>((state) => state.auth.permissions)

  const hasSettingsViewPermission = isFlagSetIn(
    [
      SettingsPermissions.VIEW_DASHBOARD,
      SettingsPermissions.VIEW_SYSTEM_SETTINGS,
      SettingsPermissions.VIEW_PASSWORD_SETTINGS,
      SettingsPermissions.VIEW_EMAIL_SETTINGS,
      SettingsPermissions.VIEW_LDAP_SETTINGS,
      SettingsPermissions.VIEW_ROLE_SETTINGS,
    ],
    permissions.settings,
  )

  const hasUserViewPermission = isFlagSetIn(
    [UserManagementPermissions.VIEW_USER_SETTINGS],
    permissions.userManagement,
  )

  const hasTeamsAndChannelsViewPermission = isFlagSetIn(
    [TeamAndChannelPermissions.VIEW_TEAM_SETTINGS, TeamAndChannelPermissions.VIEW_CHANNEL_SETTINGS],
    permissions.teamAndChannel,
  )

  const [updateUserStatus] = useUpdateUserStatusMutation()
  const [logout] = useLogoutMutation()
  const [windowApi,setWindowApi] = useState<FixMeLater>(null);

  const isAdmin =
    permissions.settings > 0 ||
    permissions.userManagement > 0 ||
    hasSettingsViewPermission ||
    hasUserViewPermission ||
    hasTeamsAndChannelsViewPermission

  const onUpdateUserStatus = (status: string) => {
    updateUserStatus({ status })
    onHide()
  }

  useEffect(()=>{
    const w = window as FixMeLater;
    if (w.api)
      setWindowApi(w.api);
  },[])

  const onLogout = async () => {

    onHide()
    clientChannel.postMessage({ type: LOGOUT })
    await logout({})

    const firebaseToken = await getFCMToken()

    try {
      if (firebaseToken) {
        await deleteFCMToken()
      }
      await signOutFirebase()
      await removeDataFromDB('vimesoft', 'fcmToken')
    } catch (error) {
        console.error('Oturum kapatma hatası:', error);
      }

    await removeDataFromDB("vimesoft",'persist:root');

    dispatch({ type: 'RESET' })
    if (socket && socket.connected) {
      socket.disconnect();
    }
    if(windowApi) {
      windowApi.send('sendLogout',true);
    }
    window.location.reload();
  }
  const onNavigateToSettings = () => {
    navigate('/admin-panel/dashboard/general-view')
    onHide()
  }
   const notificationLanguage = useAppSelector((state) => state.auth?.user?.notificationLanguage) || 'tr'

  return (
    <>
      <Wrapper isRTL={notificationLanguage === 'ar'}>
        <Status
          status={UserStatus.ONLINE}
          text={t(USER_STATUS.ONLINE)}
          onClick={onUpdateUserStatus}
        />
        <Status status={UserStatus.AWAY} text={t(USER_STATUS.AWAY)} onClick={onUpdateUserStatus} />
        <Status
          status={UserStatus.DONOTDISTURB}
          text={t(USER_STATUS.DO_NOT_DISTURB)}
          onClick={onUpdateUserStatus}
        />
        <Status
          status={UserStatus.OFFLINE}
          text={t(USER_STATUS.OFFLINE)}
          onClick={onUpdateUserStatus}
        />
        <Separator margin={{ top: 5, bottom: 5 }} />
        <BottomText cursor="pointer" onClick={onOpenAccountSettings}>
          {t(NAVIGATION.ACCOUNT_SETTINGS)}
        </BottomText>
        {isAdmin && !userOnly && (
          <BottomText cursor="pointer" onClick={onNavigateToSettings}>
            {t(NAVIGATION.SETTINGS)}
          </BottomText>
        )}
        <BottomText color={colors.red} cursor="pointer" onClick={onLogout}>
          {t(NAVIGATION.LOGOUT)}
        </BottomText>
      </Wrapper>
      <BackDrop onClick={onHide} />
    </>
  )
}

export default Options

const Wrapper = styled.div<{ isRTL?: boolean }>`
  position: absolute;
  ${({ isRTL }) => (isRTL ? 'right: 40px;' : 'left: 40px;')}
  top: 80px;
  background-color: ${colors.white};
  z-index: 7;
  border: 1px solid ${colors.light80};
  box-shadow: 4px 4px 20px 2px rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  box-sizing: border-box;
  padding: 10px 0px 10px 0px;
`

const BottomText = styled(CustomText)`
  padding: 6px 20px 6px 20px;
  white-space: nowrap;
  &:hover {
    background-color: ${colors.light50};
  }
`

export const StatusWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 20px 6px 20px;
  cursor: pointer;
  &:hover {
    background-color: ${colors.light50};
  }
`

const StatusColor = styled.span.attrs((props: { status: string }) => props)`
  border: ${({ status }) => (status === 'OFFLINE' ? '1px solid black' : '1px solid transparent')};
  border-radius: 50%;
  display: inline-block;
  width: 15px;
  height: 15px;
  background-color: ${({ status }) =>
    status === 'ONLINE'
      ? `${colors.green}`
      : status === 'AWAY'
      ? `${colors.yellow}`
      : status === 'DONOTDISTURB'
      ? `${colors.red}`
      : status === 'OFFLINE'
      ? 'transparent'
      : ''};
`

export const BackDrop = styled.div`
  position: absolute;
  right: 0;
  left: 0;
  bottom: 0;
  top: 0;
  z-index: 6;
`
